import styled, { css } from 'styled-components';
import { z } from 'zod';
import type { EditShipperDetails } from 'src/apis/organisation-service/types';
import type { OrganisationDetailsRequest } from 'src/apis/organisation-service/use-organisation-details';
import { fileSchema } from 'src/components/form/file-upload';
import { requiredOutput } from 'src/components/form/zod-utilities';
import type { Successful } from 'src/hooks/use-axios';
import type { ValidShipperData } from 'src/pages/settings/organisation/company-information/schema';
import { countries } from 'src/pages/settings/registration/components/countries';
import type {
  ConstraintViolation,
  Violation,
} from 'src/utils/is-constraint-violation';
import { createViolationError } from 'src/utils/is-constraint-violation';
import { urlRegex } from 'src/utils/validation';

export type UserRole = 'Shipper' | 'TSO' | 'SSO' | 'LSO';

const mappings: Record</* server */ string, /* client */ string> = {
  agreePublication: 'agreePublication',
  'bankAccount.iban': 'iban',
  'bankAccount.bic': 'bic',
  'bankAccount.bankName': 'bankName',
  'postalAddress.companyName': 'postalAddress.companyName',
  'postalAddress.addressLine1': 'postalAddress.addressLine1',
  'postalAddress.addressLine2': 'postalAddress.addressLine2',
  'postalAddress.postCode': 'postalAddress.postCode',
  'postalAddress.city': 'postalAddress.city',
  'postalAddress.country': 'postalAddress.country',
  'billingAddress.companyName': 'billingAddress.companyName',
  'billingAddress.addressLine1': 'billingAddress.addressLine1',
  'billingAddress.addressLine2': 'billingAddress.addressLine2',
  'billingAddress.postCode': 'billingAddress.postCode',
  'billingAddress.city': 'billingAddress.city',
  'billingAddress.country': 'billingAddress.country',
  'billingAddress.email': 'billingAddress.email',
  'billingAddress.firstName': 'billingAddress.firstName',
  'billingAddress.lastName': 'billingAddress.lastName',
  'billingAddress.department': 'billingAddress.department',
  'billingAddress.specificPostBox': 'billingAddress.specificPostBox',
  'billingAddress.postBox': 'billingAddress.postBox',
  'billingAddress.postBox.code': 'billingAddress.postBox.code',
  'billingAddress.postBox.postCode': 'billingAddress.postBox.postCode',
  'billingAddress.postBox.city': 'billingAddress.postBox.city',
};

export function getCurrentRole({
  response,
}: Successful<OrganisationDetailsRequest>) {
  const shipper = response.data._links.shipperDetails ? 'Shipper' : null;
  const TSO = response.data._links.tsoDetails ? 'TSO' : null;
  const SSO = response.data._links.ssoDetails ? 'SSO' : null;
  const LSO = response.data._links.lsoDetails ? 'LSO' : null;

  return [shipper, TSO, SSO, LSO].filter((role) => role) as UserRole[];
}

export function getOrganisationCountryLabel(countryCode: string) {
  return countries.find((country) => country.value === countryCode)!.label;
}

export function getOrganisationCountryCode(countryLabel: string) {
  return countries.find((country) => country.label === countryLabel)!.value;
}

export function friendlyFormatIBAN(iban: string): string {
  return iban.match(/.{1,4}/g)!.join(' ');
}

export function mapEditShipperPayload(
  data: ValidShipperData
): EditShipperDetails {
  return {
    agreePublication: data.agreePublication === 'yes',
    bankAccount: {
      iban: data.iban,
      bic: data.bic,
      bankName: data.bankName,
    },
    postalAddress:
      data.samePostalAddress === 'no' && data.postalAddress
        ? {
            ...data.postalAddress,
            country: getOrganisationCountryCode(
              data.postalAddress.country.label
            ),
          }
        : null,
    billingAddress:
      data.sameBillingAddress === 'no' && data.billingAddress
        ? {
            ...data.billingAddress,
            postBox:
              data.billingAddress.specificPostBox === 'yes' &&
              data.billingAddress.postBox
                ? {
                    ...data.billingAddress.postBox,
                  }
                : null,
            country: getOrganisationCountryCode(
              data.billingAddress.country.label
            ),
          }
        : null,
  };
}

export function editSthipperServerViolationToFormViolations(
  violationError: ConstraintViolation | null
): ConstraintViolation | null {
  if (!violationError) {
    return null;
  }

  const violationResults: Violation[] = [];

  const { violations } = violationError.response.data;

  violations.forEach((violation) => {
    // In rare occassion when violation.field does not exist in our mapping hardcoded array:
    // Split below fails (there is no such function on undefined value),
    // thus we need an if safe guard.
    if (mappings[violation.field]) {
      const [...fieldParts] = mappings[violation.field].split('.');
      const field = fieldParts.join('.');
      violationResults.push({
        ...violation,
        field,
      });
    }
  });

  const result = createViolationError(violationError, violationResults);

  return result;
}

const operatorSchema = z.object({
  visible: z.string().transform(requiredOutput()),
  websiteUrl: z
    .string()
    .regex(
      urlRegex,
      'URL format is incorrect. Please enter a valid URL which starts with https://'
    )
    .nullable(),
  gtcUrl: z
    .string()
    .regex(
      urlRegex,
      'URL format is incorrect. Please enter a valid URL which starts with https://'
    )
    .transform(requiredOutput()),
  shortName: z
    .string()
    .refine((data) => data.length <= 9, {
      message: '{label} cannot be more than 9 characters.',
    })
    .transform(requiredOutput()),
  smallLogo: fileSchema,
  bigLogo: fileSchema,
});

export const editOperatorSchema = z
  .object({
    contactFormUrl: z
      .string()
      .regex(
        urlRegex,
        'URL format is incorrect. Please enter a valid URL which starts with https://'
      )
      .nullable(),
  })
  .merge(operatorSchema);

export const editLsoSchema = z
  .object({
    additionalInformation: z.string().nullable(),
  })
  .merge(operatorSchema);

export const Img = styled.img<{ large?: boolean }>`
  display: block;
  margin: 0 auto;

  ${({ large }) =>
    large
      ? css`
          width: 19rem;
          height: 8.8rem;
        `
      : css`
          width: 13rem;
          height: 6rem;
        `}
  margin: 1rem 0;
  object-fit: contain;
`;
